'use client';

import { useEffect, useRef } from 'react';

function isLikelyBot() {
    const botRegex = /bot|spider|crawl|headless/i;
    return botRegex.test(navigator.userAgent);
}

function initializeDatadog() {
    const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ?? '';
    const environment = process.env.NEXT_PUBLIC_ENVIRONMENT ?? '';
    const service = process.env.NEXT_PUBLIC_DATADOG_SERVICE ?? '';
    const site = process.env.NEXT_PUBLIC_DATADOG_SITE ?? '';
    const appId = process.env.NEXT_PUBLIC_DATADOG_APP_ID ?? '';

    if (!clientToken) {
        console.error('[DatadogInitializer] No valid clientToken; skipping init.');
        return;
    }

    if (isLikelyBot()) {
        console.error('[DatadogInitializer] Bot detected, skipping init');
        return;
    }

    import('@datadog/browser-logs').then(({ datadogLogs }) => {
        datadogLogs.init({
            clientToken,
            env: environment,
            forwardErrorsToLogs: true,
            service,
            sessionSampleRate: 100,
            site,
        });
    });

    import('@datadog/browser-rum').then(({ datadogRum }) => {
        datadogRum.init({
            actionNameAttribute: 'data-custom-name',
            applicationId: appId,
            clientToken,
            site,
            service,
            env: environment,
            version: process.env.NEXT_PUBLIC_APP_VERSION || 'n/a',
            sessionSampleRate: 10,
            sessionReplaySampleRate: 5,
            defaultPrivacyLevel: 'mask-user-input',
            enableExperimentalFeatures: ['feature_flags'],
            silentMultipleInit: true,
            trackLongTasks: true,
            trackResources: true,
            traceSampleRate: 10,
            trackUserInteractions: true,
            trackingConsent: 'not-granted',
            beforeSend: () => {
                if (isLikelyBot()) {
                    console.error('[DatadogInitializer] Dropping event - userAgent matched bot');
                    return false;
                }
                return true;
            },
            excludedActivityUrls: [
                /^https?:\/\/.*\.googletagmanager\.com\/.*/,
                /^https?:\/\/.*\.optimizely\.com\/.*/,
                /^https?:\/\/.*\/heapApi\.js$/,
                /^https?:\/\/.*\.heap-api\.com\/.*/,
                /^https?:\/\/.*\.facebook\.com\/.*/,
                /^https?:\/\/.*\.google\.co\.uk\/.*/,
                /^https?:\/\/.*\.google\.com\/.*/,
                /^https?:\/\/.*\.anchor\.fm\/.*/,
                /^https?:\/\/.*\.ceros\.com\/.*/,
                /^https?:\/\/cdn\.growthbook\.io\/.*/,
                /^http?:\/\/n\.wistiaextension\.com\/.*/,
                /^https?:\/\/www\.gstatic\.com\/.*/,
                /^https?:\/\/stats\.g\.doubleclick\.net\/.*/,
            ],
        });

        datadogRum.setGlobalContextProperty(
            'commitId',
            process.env.NEXT_PUBLIC_AWS_COMMIT_ID || 'unknown',
        );
        datadogRum.setGlobalContextProperty(
            'awsAppId',
            process.env.NEXT_PUBLIC_AWS_APP_ID || 'unknown',
        );

        datadogRum.startSessionReplayRecording();

        if (!isLikelyBot()) {
            datadogRum.setTrackingConsent('granted');
        }
    });
}

export default function DatadogInitializer() {
    const hasInitialized = useRef(false);

    useEffect(() => {
        if (!hasInitialized.current) {
            hasInitialized.current = true;

            if ('requestIdleCallback' in window) {
                requestIdleCallback(() => initializeDatadog());
            } else {
                setTimeout(() => initializeDatadog(), 3000);
            }
        }
    }, []);

    return null;
}
